import { Portal } from '@material-ui/core'
import React, { useEffect, useRef, useState } from 'react'
import { FiX } from 'react-icons/fi'
import { connect, useDispatch, useSelector } from 'react-redux'
import smallLogo from '../../../assets/logo-bb-small.png'
import bigLogo from '../../../assets/logo-bb.png'
import Sidebar, { SidebarButton } from '../../../components/Template/Sidebar'
import AppVersion from '../../../components/Template/Sidebar/AppVersion'
import setDrawerRef from '../../../actions/search/setDrawerRef'
import { IStore } from '../../../reducers'
import { getSelectedLanguage } from '../../../reducers/userAccount/selectors/userLanguage'
import SidebarContent from './Menu/SidebarContent'
import {
  Booking,
  Employees,
  Finance,
  Help,
  Integrations,
  Premium,
  Pricing,
  Profile,
  Refresh,
  Settings,
  Widgets,
} from './Menu'
import Invoices from './Menu/Invoices'
import {
  isNewAccount,
} from '../../../reducers/userAccount/selectors/userAccount'
import useIsScope from '../../../hooks/useIsScope'
import { PermissionScope } from '../../Employees'
import { isWorker } from '../../../reducers/userAccount/selectors/userAccount'
import { isAndroidWebView, isIOSWebView } from '../../../components/DetectBrowser/DetectBrowser'

const isWebView = () => {
  try {
    return isAndroidWebView() || isIOSWebView()
  } catch (e) {
    return false
  }
}

function SidebarContainer(props: ISidebarProps) {
  const { size = 1, lang } = props
  const [timeout, setTimeOut] = useState(null)
  const [blockToggle, setBlockToggle] = useState(false)
  // const TaskCount = useSelector(getTaskBadge)

  const scopes = useIsScope({
    premium: [PermissionScope.SCOPE_CAN_BUY_PREMIUM],
    bookingEngine: [PermissionScope.SCOPE_CAN_USE_WIDGET],
    profile: [PermissionScope.SCOPE_CAN_USE_PROFILE],
    prices: [PermissionScope.SCOPE_CAN_USE_PRICING_AND_RESTRICTIONS],
    config: [PermissionScope.SCOPE_CAN_USE_PRICING_SETTINGS],
    fvat: [PermissionScope.SCOPE_CAN_USE_INVOICES],
    fvatConfig: [PermissionScope.SCOPE_CAN_USE_INVOICES_SETTINGS],
    export: [PermissionScope.SCOPE_CAN_USE_EXPORT],
    reports: [PermissionScope.SCOPE_CAN_USE_FINANCIAL_REPORTS],
    occupancy: [PermissionScope.SCOPE_CAN_USE_OCCUPANCY_REPORTS],
    integration: [PermissionScope.SCOPE_CAN_USE_INTEGRATIONS_SETTINGS],
    settings: [PermissionScope.SCOPE_CAN_USE_GLOBAL_SETTINGS],
    permissions: [PermissionScope.SCOPE_CAN_USE_PERMISSIONS],
  })
  const worker = useSelector(isWorker)
  const canShowSettings = scopes.settings || (worker && !isWebView())

  const isSidebarOpen = useSelector(
    (state: IStore) => state.bookingCalendar.isSidebarOpen,
  )
  const blockSidebar = useSelector(
    (state: IStore) => state.bookingCalendar.isSidebarBlock,
  )

  const isNew = useSelector(isNewAccount)
  // const pricelistVersion = useSelector(getUserPriceListVersion)
  // const newPriceList = pricelistVersion === '2'

  const toggleWithBlock = (t: boolean) => () => {
    if (blockToggle) {
      setBlockToggle(false)
      clearTimeout(timeout)
      return
    }
    if (!t) {
      setBlockToggle(false)
      clearTimeout(timeout)
    }
    !blockSidebar && toggleSidebar(t)
  }
  const ref = useRef(null as HTMLDivElement)
  const dispatch = useDispatch()

  function toggleSidebar(t) {
    dispatch({
      isSidebarBlock: blockSidebar,
      isSidebarOpen: t,
      type: 'toggle left sidebar',
    })
  }

  function toggle() {
    dispatch({
      isSidebarBlock: blockSidebar,
      isSidebarOpen: !isSidebarOpen,
      type: 'toggle left sidebar',
    })
  }

  useEffect(() => {
    if (!blockSidebar || window.innerWidth < 1024) {
      setBlockToggle(true)
      setTimeOut(
        setTimeout(() => {
          setBlockToggle(false)
        }, 5000),
      )
      dispatch({
        isSidebarBlock: blockSidebar,
        isSidebarOpen: false,
        type: 'toggle left sidebar',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(window as any).routerHistory.location.pathname])

  function setRef() {
    dispatch(setDrawerRef(ref))
  }

  useEffect(() => {
    if (ref.current) {
      setRef()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.current])

  return (
    <React.Fragment>
      <Portal>
        <React.Fragment>
          <Sidebar
            size={size}
            ref={ref}
            isSidebarOpen={isSidebarOpen}
            onMouseOver={toggleWithBlock(true)}
            onMouseLeave={toggleWithBlock(false)}
            key={String(lang)}
          >
            <SidebarButton size={size} as={'div'} top={true} alignCenter={true}>
              <FiX onClick={toggle} />
              <img src={bigLogo} className={'big-logo'} alt={'BedBooking'} />
              <img
                src={smallLogo}
                className={'small-logo'}
                alt={'BedBooking'}
              />
            </SidebarButton>
            <SidebarContent size={size}>
              {scopes.premium && (
                <Premium size={size} className={'navigation-sidebar-item-0'} />
              )}
              <Booking
                size={size}
                isSidebarOpen={isSidebarOpen}
                count={0}
                className={'navigation-sidebar-item-1'}
              />
              {(scopes.prices || scopes.config) && (
                <Pricing
                  size={size}
                  isSidebarOpen={isSidebarOpen}
                  className={'navigation-sidebar-item-5'}
                />
              )}
              {(scopes.reports || scopes.export || scopes.occupancy) && (
                <Finance
                  size={size}
                  isSidebarOpen={isSidebarOpen}
                  className={'navigation-sidebar-item-6'}
                />
              )}
              {(scopes.fvat || scopes.fvatConfig) && (
                <Invoices size={size} isSidebarOpen={isSidebarOpen} />
              )}
              {scopes.bookingEngine && <Widgets size={size} badge={!isNew} />}

              {scopes.integration && (
                <Integrations
                  size={size}
                  isSidebarOpen={isSidebarOpen}
                  className={'navigation-sidebar-item-8'}
                />
              )}
              {scopes.permissions && (
                <Employees isSidebarOpen={isSidebarOpen} size={size} />
              )}
              {scopes.profile && <Profile size={size} />}
              {canShowSettings && (
                <Settings size={size} className={'navigation-sidebar-item-9'} />
              )}

              {/*<Tasks*/}
              {/*  isSidebarOpen={isSidebarOpen}*/}
              {/*  count={TaskCount}*/}
              {/*  size={size}*/}
              {/*/>*/}

              {/*<Clients size={size} />*/}
              {/*<Offer*/}
              {/*  size={size}*/}
              {/*  isSidebarOpen={isSidebarOpen}*/}
              {/*  className={'navigation-sidebar-item-4'}*/}
              {/*/>*/}

              {/*<Statistics*/}
              {/*  size={size}*/}
              {/*  isSidebarOpen={isSidebarOpen}*/}
              {/*  className={'navigation-sidebar-item-7'}*/}
              {/*/>*/}
              {/*{!isNew && <OldWidgets size={size} />}*/}

              {/*<Templates size={size} />*/}
              <Help
                size={size}
                className={'navigation-sidebar-item-5'}
                isSidebarOpen={isSidebarOpen}
              />
              <Refresh size={size} />
            </SidebarContent>
            {isSidebarOpen && (
              <AppVersion
                size={size}
                version={process.env.REACT_APP_APP_VERSION}
                isSidebarOpen={isSidebarOpen}
              />
            )}
          </Sidebar>
        </React.Fragment>
      </Portal>
    </React.Fragment>
  )
}

export default connect((state: IStore) => ({
  lang: getSelectedLanguage(state),
}))(SidebarContainer)

export interface ISidebarProps {
  size?: number
  lang?: string
}
